// Contract values are stored as integer minor units (cents, paise) plus an ISO 4217 code.

const FALLBACK_CURRENCIES = [
  "AED", "AUD", "BRL", "CAD", "CHF", "CNY", "CZK", "DKK", "EUR", "GBP", "HKD", "IDR", "ILS", "INR", "JPY",
  "KRW", "MXN", "MYR", "NOK", "NZD", "PHP", "PLN", "SAR", "SEK", "SGD", "THB", "TRY", "USD", "ZAR",
];

let cached: Set<string> | null = null;

export function currencyCodes(): string[] {
  return [...currencySet()].sort();
}

function currencySet(): Set<string> {
  if (cached) return cached;
  let codes: string[] = FALLBACK_CURRENCIES;
  try {
    const supported = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.("currency");
    if (supported && supported.length > 0) codes = supported;
  } catch {
    // Older runtimes: keep the fallback list.
  }
  cached = new Set(codes);
  return cached;
}

export function isKnownCurrency(code: unknown): code is string {
  return typeof code === "string" && /^[A-Z]{3}$/.test(code) && currencySet().has(code);
}

/** Number of decimal places a currency uses (USD 2, JPY 0, BHD 3). */
export function currencyDigits(currency: string): number {
  try {
    return new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    return 2;
  }
}

/** Largest accepted value in major units: one trillion. */
export const MAX_MAJOR_UNITS = 1_000_000_000_000;

export type MoneyParse = { ok: true; minor: number | null } | { ok: false; error: string };

/**
 * Parses what people actually type: "$1,200.50", "1.200,50 €", "1,50,000", "12k" is rejected.
 * Empty input means "no value".
 */
export function parseMoneyInput(input: string, currency: string): MoneyParse {
  const raw = input.trim();
  if (raw === "") return { ok: true, minor: null };
  if (/^\s*-|\(.*\)|-\s*\d/.test(raw)) return { ok: false, error: "The value can't be negative." };
  if (/\d\s*[kmb]\b/i.test(raw)) return { ok: false, error: "Type the full number, for example 12000 instead of 12k." };

  // Letters between digits ("1e30", "12o0") mean it isn't a plain number.
  if (/\d\s*\p{L}+\s*\d/u.test(raw)) return { ok: false, error: "Enter a number, for example 1200.50." };
  // Drop currency words with their abbreviation dot ("Rs.", "USD"), symbols and spaces.
  const cleaned = raw.replace(/\p{L}+\.?/gu, "").replace(/[^\d.,]/g, "");
  if (!/\d/.test(cleaned)) return { ok: false, error: "Enter a number, for example 1200.50." };

  const lastDot = cleaned.lastIndexOf(".");
  const lastComma = cleaned.lastIndexOf(",");
  let decimalSep: "." | "," | null = null;
  if (lastDot >= 0 && lastComma >= 0) {
    decimalSep = lastDot > lastComma ? "." : ",";
  } else if (lastComma >= 0) {
    // "12,50" is a decimal comma; "1,200" and "1,50,000" are grouping.
    const commas = cleaned.split(",").length - 1;
    const after = cleaned.length - lastComma - 1;
    decimalSep = commas === 1 && after > 0 && after <= 2 ? "," : null;
  } else if (lastDot >= 0) {
    const dots = cleaned.split(".").length - 1;
    decimalSep = dots === 1 ? "." : null;
  }

  let intPart: string;
  let fracPart = "";
  if (decimalSep) {
    const idx = cleaned.lastIndexOf(decimalSep);
    intPart = cleaned.slice(0, idx);
    fracPart = cleaned.slice(idx + 1);
  } else {
    intPart = cleaned;
  }
  intPart = intPart.replace(/[.,]/g, "");
  if (/[.,]/.test(fracPart)) return { ok: false, error: "That number has too many separators." };
  if (intPart === "") intPart = "0";

  const digits = currencyDigits(currency);
  if (fracPart.length > digits) {
    return {
      ok: false,
      error: digits === 0 ? `${currency} amounts have no decimal places.` : `${currency} amounts have at most ${digits} decimal places.`,
    };
  }

  const major = Number(intPart);
  if (!Number.isFinite(major) || major > MAX_MAJOR_UNITS) {
    return { ok: false, error: "That value is too large." };
  }
  const minor = Number(intPart) * 10 ** digits + Number(fracPart.padEnd(digits, "0") || "0");
  if (!Number.isSafeInteger(minor)) return { ok: false, error: "That value is too large." };
  return { ok: true, minor };
}

export function formatMoney(minor: number | null | undefined, currency: string | null | undefined, locale?: string): string {
  if (minor === null || minor === undefined || !currency) return "—";
  const digits = currencyDigits(currency);
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency }).format(minor / 10 ** digits);
  } catch {
    return `${(minor / 10 ** digits).toFixed(digits)} ${currency}`;
  }
}

/** Minor units back to an editable string, e.g. 120050 USD → "1200.50". */
export function minorToInput(minor: number | null | undefined, currency: string | null | undefined): string {
  if (minor === null || minor === undefined) return "";
  const digits = currencyDigits(currency || "USD");
  return (minor / 10 ** digits).toFixed(digits);
}
